import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FaPencilAlt, FaTrash } from "react-icons/fa";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import axios from "../../axiosConfig";
import { useNavigate } from "react-router-dom";
import ListPageLayout from "../common/ListPageLayout";
import TruncatedText from "../common/TruncatedText";

const NAME_MAX_CHARS = 80;
const KEY_MAX_CHARS = 60;

const SurveyPeriodsList = () => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Sorting (same pattern as other lists)
  const [sortKey, setSortKey] = useState("name"); // name | key | start_date | end_date
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  const toast = useToast();
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const subTextColor = useColorModeValue("gray.600", "gray.400");

  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const res = await axios.get("/survey_periods");
        setPeriods(res.data ?? []);
      } catch (error) {
        toast({
          title: "Failed to load periods.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPeriods();
  }, [toast]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this period?")) return;

    try {
      await axios.delete(`/survey_periods/${id}`);
      setPeriods((prev) => prev.filter((p) => p.id !== id));
      toast({
        title: "Period deleted.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to delete period.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  /* ---------------- sorting helpers ---------------- */

  const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

  const toSortableDate = (v) => {
    // supports ISO dates: "YYYY-MM-DD" (string compare works),
    // but we return a consistent string anyway
    const s = (v ?? "").toString().trim();
    return s || "";
  };

  const sortedPeriods = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;

    const valueFor = (p) => {
      switch (sortKey) {
        case "key":
          return normalize(p.key);
        case "start_date":
          return toSortableDate(p.start_date);
        case "end_date":
          return toSortableDate(p.end_date);
        case "name":
        default:
          return normalize(p.name);
      }
    };

    return [...periods].sort((a, b) => {
      const av = valueFor(a);
      const bv = valueFor(b);

      // Dates sort lexicographically fine if they're YYYY-MM-DD
      const cmp =
        typeof av === "string" && typeof bv === "string"
          ? av.localeCompare(bv)
          : 0;

      if (cmp !== 0) return cmp * dir;

      // stable-ish fallback
      return normalize(a.name).localeCompare(normalize(b.name));
    });
  }, [periods, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIndicator = ({ active }) => {
    if (!active) return null;
    return sortDir === "asc" ? (
      <TriangleUpIcon ml={2} boxSize="10px" />
    ) : (
      <TriangleDownIcon ml={2} boxSize="10px" />
    );
  };

  /* ---------------- UI renderers ---------------- */

  const renderCard = (period) => (
    <Box
      key={period.id}
      p={4}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      shadow="sm"
      bg={cardBg}
      _hover={{ shadow: "md" }}
    >
      <VStack align="start" spacing={3} w="100%">
        <HStack justify="space-between" w="100%" align="start">
          <Box minW={0}>
            <TruncatedText
              value={period.name || "-"}
              maxChars={NAME_MAX_CHARS}
              fontWeight="bold"
              fontSize="md"
            />
            <Text fontSize="sm" color={subTextColor}>
              Key: {period.key || "—"}
            </Text>
          </Box>

          <Badge variant="subtle" colorScheme="purple" borderRadius="md">
            Survey period
          </Badge>
        </HStack>

        <HStack spacing={6} flexWrap="wrap">
          <Box>
            <Text fontSize="sm" color={subTextColor}>
              Start
            </Text>
            <Text>{period.start_date || "—"}</Text>
          </Box>

          <Box>
            <Text fontSize="sm" color={subTextColor}>
              End
            </Text>
            <Text>{period.end_date || "—"}</Text>
          </Box>
        </HStack>

        <HStack w="100%" justify="flex-end" spacing={2}>
          <Tooltip label="Edit" hasArrow>
            <IconButton
              icon={<FaPencilAlt />}
              size="sm"
              aria-label="Edit"
              title="Edit"
              variant="outline"
              colorScheme="yellow"
              onClick={() => navigate(`/app/survey_periods/${period.id}/edit`)}
            />
          </Tooltip>

          <Tooltip label="Delete" hasArrow>
            <IconButton
              icon={<FaTrash />}
              size="sm"
              colorScheme="red"
              variant="outline"
              aria-label="Delete"
              title="Delete"
              onClick={() => handleDelete(period.id)}
            />
          </Tooltip>
        </HStack>
      </VStack>
    </Box>
  );

  const renderTable = () => (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      bg={cardBg}
      shadow="sm"
      overflow="hidden"
    >
      <TableContainer w="100%" maxW="100%" minW={0} overflowX="auto">
        <Table
          variant="unstyled"
          size="md"
          tableLayout="fixed"
          w="100%"
          maxW="100%"
          sx={{
            "th, td": { borderBottom: "1px solid", borderColor: "gray.100" },
            "tbody tr:hover": { background: "gray.50" },
          }}
        >
          <colgroup>
            <col style={{ width: "34%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>

          <Thead>
            <Tr
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: cardBg,
              }}
            >
              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("name")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Name <SortIndicator active={sortKey === "name"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("key")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Key <SortIndicator active={sortKey === "key"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("start_date")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Start <SortIndicator active={sortKey === "start_date"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("end_date")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                End <SortIndicator active={sortKey === "end_date"} />
              </Th>

              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Actions
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {sortedPeriods.map((period) => (
              <Tr key={period.id}>
                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText
                      value={period.name || "-"}
                      maxChars={NAME_MAX_CHARS}
                      fontWeight="semibold"
                    />
                  </Box>
                </Td>

                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText value={period.key || "—"} maxChars={KEY_MAX_CHARS} />
                  </Box>
                </Td>

                <Td py={3}>
                  <Text>{period.start_date || "—"}</Text>
                </Td>

                <Td py={3}>
                  <Text>{period.end_date || "—"}</Text>
                </Td>

                <Td py={3}>
                  <HStack spacing={2}>
                    <Tooltip label="Edit" hasArrow>
                      <IconButton
                        icon={<FaPencilAlt />}
                        size="sm"
                        aria-label="Edit"
                        title="Edit"
                        variant="outline"
                        colorScheme="yellow"
                        onClick={() => navigate(`/app/survey_periods/${period.id}/edit`)}
                      />
                    </Tooltip>

                    <Tooltip label="Delete" hasArrow>
                      <IconButton
                        icon={<FaTrash />}
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        aria-label="Delete"
                        title="Delete"
                        onClick={() => handleDelete(period.id)}
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  const actions = (
    <HStack spacing={3} flexWrap="wrap">
      {isMobile && (
        <Select
          size="sm"
          w="220px"
          value={`${sortKey}:${sortDir}`}
          onChange={(e) => {
            const [k, d] = e.target.value.split(":");
            setSortKey(k);
            setSortDir(d);
          }}
        >
          <option value="name:asc">Name (A–Z)</option>
          <option value="name:desc">Name (Z–A)</option>
          <option value="key:asc">Key (A–Z)</option>
          <option value="key:desc">Key (Z–A)</option>
          <option value="start_date:asc">Start (Old–New)</option>
          <option value="start_date:desc">Start (New–Old)</option>
          <option value="end_date:asc">End (Old–New)</option>
          <option value="end_date:desc">End (New–Old)</option>
        </Select>
      )}

      <Button
        colorScheme="blue"
        variant="outline"
        size="sm"
        onClick={() => navigate("/app/survey_periods/new")}
      >
        Add New Period
      </Button>
    </HStack>
  );

  return (
    <ListPageLayout title="Survey Periods" actions={actions} contentProps={{ maxW: "100%", minW: 0 }}>
      {loading ? (
        <Flex justify="center" mt={10}>
          <Spinner size="xl" color="orange.400" />
        </Flex>
      ) : sortedPeriods.length > 0 ? (
        isMobile ? (
          <VStack spacing={4} mt={2} align="stretch">
            {sortedPeriods.map(renderCard)}
          </VStack>
        ) : (
          <Box mt={2}>{renderTable()}</Box>
        )
      ) : (
        <Text mt={10} textAlign="center">
          No survey periods found.
        </Text>
      )}
    </ListPageLayout>
  );
};

export default SurveyPeriodsList;